import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { financialsApi, onboardingAdminApi } from '../services/api'
import { useAuth } from './AuthContext'

const NotificationContext = createContext({
  pendingRequestCount: 0,
  onboardingActionCount: 0,
  refreshNotifications: () => {}
})

export function NotificationProvider({ children }) {
  const { user, isAdmin } = useAuth()
  const [pendingRequestCount, setPendingRequestCount] = useState(0)
  const [onboardingActionCount, setOnboardingActionCount] = useState(0)
  // Same access as the Onboarding nav item
  const seesOnboarding = user?.access_to_clients === true || user?.access_to_clients === 1

  const refreshNotifications = useCallback(async () => {
    if (!user || !isAdmin) {
      setPendingRequestCount(0)
    } else {
      try {
        const response = await financialsApi.getRequestStats()
        if (response.data?.stats) {
          // Count pending + in_progress as actionable items
          // Parse as integers since API may return strings
          const pending = parseInt(response.data.stats.pending) || 0
          const inProgress = parseInt(response.data.stats.in_progress) || 0
          setPendingRequestCount(pending + inProgress)
        }
      } catch (error) {
        console.error('Failed to fetch notification count:', error)
      }
    }

    // Onboarding applications waiting on the team (new / replied, or ViciDial access received)
    if (!user || !seesOnboarding) {
      setOnboardingActionCount(0)
    } else {
      try {
        const response = await onboardingAdminApi.getSummary()
        setOnboardingActionCount(Number(response.data?.needsAction) || 0)
      } catch (error) {
        console.error('Failed to fetch onboarding count:', error)
      }
    }
  }, [user, isAdmin, seesOnboarding])

  // Poll for notifications every 30 seconds
  useEffect(() => {
    if (!user || (!isAdmin && !seesOnboarding)) return

    // Initial fetch
    refreshNotifications()

    // Set up polling
    const interval = setInterval(refreshNotifications, 30000)

    return () => clearInterval(interval)
  }, [user, isAdmin, seesOnboarding, refreshNotifications])

  return (
    <NotificationContext.Provider value={{ pendingRequestCount, onboardingActionCount, refreshNotifications }}>
      {children}
    </NotificationContext.Provider>
  )
}

export function useNotifications() {
  return useContext(NotificationContext)
}
